import _ from 'lodash';
import { colors } from '@nerdwallet/base-styles/variables';
import { getValidationFn } from './validations';
import { DATA_DEPENDENCY_LABELS, DATA_DEPENDENCY_TYPES } from './constants';
import adminCategoryPages from './credit-cards/admin-category-pages';

/**
 * CUSTOMIZABLE INPUTS
 *
 * For adding a new, custom input to a component. These inputs will need some configuration within the component itself to add labels
 * and process the input from the builder.
 */

/**
 * A single-line text input for a small amount of text, i.e. a title or a heading. Yields a string for the component that uses it.
 *
 * @example
 *  YourComponent.getEditingConfig = () => [
 *    stringInputSpec('header', 'Header', { help: "I'm a help tooltip!" }),
 *  ];
 *
 * @param {String} fieldName - the name of the prop that the component will access the input from
 * @param {String} fieldLabel - the label that will display in the builder
 * @param {Object} options - an object containing additional optional fields, the options being:
 *    @param {String} help - help text, which appears in a tooltip
 *    @param {Boolean} isProp - whether the input should be passed as a prop to the component. defaults to true, can likely be left
 *                              true unless the field connects to a data dependency
 *
 * @returns {Object} - a standard mapping that is further processed in generateSettingUIConfigFromSpec (below) before being passed to
 * react-config-builder through the editor container
 */
export const stringInputSpec = (fieldName, fieldLabel, options = {}) => ({
  fieldName,
  fieldLabel,
  dataType: 'string',
  fieldType: 'input',
  ...options,
});

/**
 * A single-line input for numbers. Yields a number for the component that uses it.
 *
 * @example
 *  YourComponent.getEditingConfig = () => [
 *    numberInputSpec('numWeeks', 'Number of Weeks'),
 *  ];
 *
 * @param {String} fieldName - the name of the prop that the component will access the input from
 * @param {String} fieldLabel - the label that will display in the builder
 * @param {Object} options - an object containing additional optional fields, the options being:
 *    @param {String} help - help text, which appears in a tooltip
 *    @param {Boolean} isProp - whether the input should be passed as a prop to the component. defaults to true, can likely be left
 *                              true unless the field connects to a data dependency
 *
 * @returns {Object} - a standard mapping that is further processed in generateSettingUIConfigFromSpec (below) before being passed to
 * react-config-builder through the editor container
 */
export const numberInputSpec = (fieldName, fieldLabel, options = {}) => ({
  fieldName,
  fieldLabel,
  dataType: 'number',
  fieldType: 'input',
  ...options,
});

/**
 * A multi-line text input for a large amount of text, i.e. paragraphs. Yields a string for the component that uses it.
 *
 * @example
 *  YourComponent.getEditingConfig = () => [
 *    stringTextAreaSpec('text', 'Text', { help: 'Write your paragraph out here!' }),
 *  ];
 *
 * @param {String} fieldName - the name of the prop that the component will access the input from
 * @param {String} fieldLabel - the label that will display in the builder
 * @param {Object} options - an object containing additional optional fields, the options being:
 *    @param {String} help - help text, which appears in a tooltip
 *    @param {Boolean} isProp - whether the input should be passed as a prop to the component. defaults to true, can likely be left
 *                              true unless the field connects to a data dependency
 *
 * @returns {Object} - a standard mapping that is further processed in generateSettingUIConfigFromSpec (below) before being passed to
 * react-config-builder through the editor container
 */
export const stringTextAreaSpec = (fieldName, fieldLabel, options = {}) => ({
  ...stringInputSpec(fieldName, fieldLabel, options),
  fieldType: 'textarea',
});

/**
 * Repeatable supports complex, extendable editing configs for things such as lists or tables with mutliple entries. A repeatable
 * will yield an array of objects of a standard shape that are passed to the component under the specified prop name. For a repeatable,
 * you define a set of inputs which represent the editing config that will be used for each repeated item.
 *
 * For example, consider a component Table with a prop rows, where each row consists of a product ID and a title:
 *
 * Table.getEditingConfig = () => [
 *   repeatable('rows', 'Rows', 'Row', () => [
 *     stringInputSpec('productId'),
 *     stringInputSpec('title'),
 *   ],
 * ];
 *
 * Will yield the following prop structure:
 * {
 *   rows: [
 *    { productId: 'foo', title: 'bar' },
 *    { productId: 'baz', title: 'keh' },
 *   ],
 * }
 *
 * You can also dynamically generate the contents of the repeatable, as in the following editing config:
 * Table.getEditingConfig = componentDocument => [
 *   repeatable('rows', 'Rows', 'Row', index => [
 *     stringInputSpec('productId'),
 *     get(componentDocument, `rows.${index}.productId`) === '3000' ? stringInputSpec('title') : stringInputSpect('subtitle'),
 *   ],
 * ];
 * This allows the contents of one item in a repeatable to dynamically update without affecting the other items.
 *
 * Repeatable will render a form for each repeated item, with "add another" / "delete this one" buttons.
 *
 * @param {String} fieldName - the name of the prop that the component will access the input from
 * @param {String} fieldLabel - the label that will display in the builder
 * @param {String} singularLabel - the label for a single element in a repeatable
 * @param {Function} elements - a function that takes the index of the current item as a prop and returns an array of editing configs
 * @param {Object} options - an object containing additional optional fields, the options being:
 *    @param {String} help - help text, which appears in a tooltip
 *    @param {Boolean} isProp - whether the input should be passed as a prop to the component. defaults to true, can likely be left
 *                              true unless the field connects to a data dependency
 *
 * @returns {Object} - a standard mapping that is further processed in generateSettingUIConfigFromSpec (below) before being passed to
 * react-config-builder through the editor container
 */
export const repeatable = (
  fieldName,
  fieldLabel,
  singularLabel,
  elements,
  options = {}
) => ({
  fieldType: 'repeatable',
  fieldName,
  fieldLabel,
  singularLabel,
  elements,
  isProp: !(options.isProp === false),
  options,
});

export const group = (fieldLabel, elements, options = {}) => ({
  fieldType: 'group',
  fieldLabel,
  elements,
  options,
});

export const wrapper = (elements, label, options = {}) => ({
  fieldType: 'wrapper',
  elements,
  label,
  options,
});

export const row = (fieldName, fieldLabel, elements, options = {}) => ({
  fieldName,
  fieldType: 'row',
  fieldLabel,
  elements,
  options,
});

export const groupButton = (fieldName, fieldLabel, elements, options = {}) => ({
  fieldName,
  fieldLabel,
  dataType: 'string',
  elements,
  options,
  fieldType: 'groupButton',
});

export const collapsable = (fieldName, fieldLabel, elements, options = {}) => ({
  fieldName,
  fieldType: 'collapsable',
  fieldLabel,
  elements,
  options,
});

export const colorPicker = (fieldName, fieldLabel, options = {}) => ({
  fieldName,
  fieldLabel,
  dataType: 'string',
  options,
  fieldType: 'colorPicker',
});

/**
 * A dropdown select, with configurable items to choose between. Yields a string for the component that uses it.
 *
 * @example
 *  YourComponent.getEditingConfig = () => [
 *    stringSelectSpec('backgroundSize', 'Background Size', [
 *      { label: 'Contain', value: 'contain' },
 *      { label: 'Cover', value: 'cover' },
 *      { label: 'Auto', value: 'auto' },
 *    ], {
 *      help: 'Only applies to Desktop and Tablet',
 *    }),
 *  ];
 *
 * @param {String} fieldName - the name of the prop that the component will access the input from
 * @param {String} fieldLabel - the label that will display in the builder
 * @param {Array} items - an array of objects of form { value, label } consisting of the options that should display in the dropdown
 * @param {Object} options - an object containing additional optional fields, the options being:
 *    @param {String} help - help text, which appears in a tooltip
 *    @param {Boolean} isProp - whether the input should be passed as a prop to the component. defaults to true, can likely be left
 *                              true unless the field connects to a data dependency
 *
 * @returns {Object} - a standard mapping that is further processed in generateSettingUIConfigFromSpec (below) before being passed to
 * react-config-builder through the editor container
 */
export const stringSelectSpec = (
  fieldName,
  fieldLabel,
  items = [{ value: '', label: 'Unselected' }],
  options = {}
) => ({
  fieldName,
  fieldLabel,
  dataType: 'string',
  fieldType: 'select',
  items,
  ...options,
});

/**
 * A single block checkbox. Yields a boolean for the component that uses it.
 *
 * @example
 *  YourComponent.getEditingConfig = () => [
 *    boolCheckboxSpec('wideSplash', 'Smaller margins', { help: 'Allow text to expand closer to the edge of the screen.' }),
 *  ];
 *
 * @param {String} fieldName - the name of the prop that the component will access the input from
 * @param {String} fieldLabel - the label that will display in the builder
 * @param {Object} options - an object containing additional optional fields, the options being:
 *    @param {String} help - help text, which appears in a tooltip
 *    @param {Boolean} isProp - whether the input should be passed as a prop to the component. defaults to true, can likely be left
 *                              true unless the field connects to a data dependency
 *
 * @returns {Object} - a standard mapping that is further processed in generateSettingUIConfigFromSpec (below) before being passed to
 * react-config-builder through the editor container
 */
export const boolCheckboxSpec = (fieldName, fieldLabel, options = {}) => ({
  fieldName,
  fieldLabel,
  fieldType: 'checkbox',
  ...options,
  // By default, create a single checkbox with boolean value.
  // Checkbox takes its `items` as a prop named `options`, go figure.
  options: options.items || [
    {
      label: fieldLabel,
      value: 'true',
      disabled: options?.disabled || false,
    },
  ],
});

/**
 * PRECONFIGURED INPUTS
 *
 * These are some standard, preconfigured inputs that many components use. They do not require further configuration in the components
 * that use them.
 *
 * @example
 * YourComponent.getEditingConfig = () => [titleInput, subtitleInput, textColorPicker];
 */

// A string preheader, traditionally smaller text directly above a header/title. Yields a string for the component that uses it.
export const preheaderInput = stringTextAreaSpec('preheader', 'Preheader');
export const titleInput = stringTextAreaSpec('title', 'Title');
export const subtitleInput = stringTextAreaSpec('subtitle', 'Subtitle');
export const anchorIdInput = stringInputSpec('anchorId', 'Jump link ID', {
  help: 'If you need to jump to this section from elsewhere on the page, type an id here and use the same id in your jump link, prepended with "#". E.g., write "chart-section" here, and [Jump to chart!](#chart-section) on another part of the page.',
});

/**
 * COLOR PICKERS
 *
 * To expand supported colors, add your color to the options list for a picker and it'll display in the builder.
 * If it's a custom color, those should be defined in ./get-valid-color.js. Colors in base-styles and react-colors are
 * automatically supported.
 */

// Picks background color for a component. Yields a string for the component that uses it.
export const backgroundColorPicker = {
  fieldLabel: 'Background color',
  fieldName: 'backgroundColor',
  dataType: 'string',
  fieldType: 'select',
  items: [
    {
      value: '',
      label: 'None',
    },
    {
      value: 'teal',
      label: 'Teal',
    },
    {
      value: 'neutral-darkest',
      label: 'Neutral Darkest',
    },
    {
      value: 'neutral-lighter',
      label: 'Neutral Lighter',
    },
    {
      value: 'neutral-lightest',
      label: 'Neutral Lightest',
    },
    {
      value: 'PINKISH',
      label: 'Pinkish',
    },
    {
      value: 'MUSTARD',
      label: 'Mustard',
    },
    {
      value: 'CLEMENTINE',
      label: 'Clementine',
    },
    {
      value: 'green',
      label: 'Green',
    },
    {
      value: 'nw-green',
      label: 'NW Green',
    },
    {
      value: 'green-light',
      label: 'Green Light',
    },
    {
      value: 'green-lightest',
      label: 'Green Lightest',
    },
    {
      value: 'green-dark',
      label: 'Green Dark',
    },
  ],
};

/**
 * Helper used to get list of colors for SC components.
 * Created from @nerdwallet/base-styles/variables will return a list like:
 * green, green-light, green-lightest, etc.
 */

const mappedColors = Object.keys(colors).map((color) => {
  return { value: color, label: color };
});

export const scBackgroundColorPicker = {
  fieldLabel: 'Background Color',
  fieldName: 'backgroundColor',
  dataType: 'string',
  fieldType: 'select',
  items: mappedColors,
};

// Picks text color for a component. Yields a string for the component that uses it.
export const textColorPicker = {
  fieldName: 'textColor',
  fieldLabel: 'Text color',
  dataType: 'string',
  fieldType: 'select',
  items: [
    {
      value: 'neutral-darkest',
      label: 'Neutral Darkest',
    },
    {
      value: 'neutral-darker',
      label: 'Neutral Darker',
    },
    {
      value: 'white',
      label: 'White',
    },
    {
      value: 'teal',
      label: 'Teal',
    },
    {
      value: 'green',
      label: 'Green',
    },
    {
      value: 'green-dark',
      label: 'Dark Green',
    },
  ],
};

// If your component takes different types of product data as an input, this type picker lets users select what type of product data
// the component should use
export const dataDependencyTypePicker = stringSelectSpec(
  'dataDependency.type',
  'Dependencies - Data Type',
  [
    {
      label: 'Pick one if needed',
      value: '',
    },
  ].concat(
    _.values(DATA_DEPENDENCY_TYPES)
      // Remove checking and savings from list
      .filter(
        (dataDep) =>
          [
            DATA_DEPENDENCY_TYPES.CHECKING,
            DATA_DEPENDENCY_TYPES.SAVINGS,
          ].indexOf(dataDep) === -1
      )
      .map((dataDep) => ({
        label: DATA_DEPENDENCY_LABELS[dataDep],
        value: dataDep,
      }))
  ),
  { isProp: false }
);

// An input for product IDs when you have multiple product IDs associated with your component, like with a table. Yields an array.
export const dataDependencyIdsAdder = repeatable(
  'dataDependency.data.ids',
  'Dependencies - Products',
  'Product',
  () => [stringInputSpec('id', 'Product slug or id')],
  { isProp: false }
);

export const dataDependencyCommaSeparatedStringInput = stringTextAreaSpec(
  'dataDependency.data.ids',
  'Product Ids (comma-separated)',
  {
    isProp: false,
    help: 'Please input a comma-separated list of ids, i.e. "3214, 2140, 3200"',
  }
);

// Inputs for a product ID for when you have a single ID associated with your component, split by whether the ID is a string or
// a number. Yields a string and a number respectively.
export const dataDependencyIdInput = stringInputSpec(
  'dataDependency.data.ids.0.id',
  'Product Id',
  { isProp: false }
);
export const dataDependencyNumericIdInput = numberInputSpec(
  'dataDependency.data.ids.0.id',
  'Product Id',
  { isProp: false }
);

export const formInputChangeEventFields = [
  stringInputSpec('formName', 'tracking formName', {
    help: 'For the collapsible event',
  }),
  stringInputSpec('fieldName', 'tracking fieldName', {
    help: 'For the collapsible event',
  }),
  stringInputSpec('fieldValue', 'tracking fieldValue', {
    help: 'For the collapsible event',
  }),
];

/**
 * CREDIT CARD COMPLIANCE - config for mandatory categorization of credit card tables
 */
export const adminCategoryPageSelectInput = stringSelectSpec(
  'adminCategoryPage',
  'Credit Card Categorization',
  [{ label: 'Select a category', value: null }].concat(
    Object.keys(adminCategoryPages).map((category) => ({
      label: _.get(adminCategoryPages[category], 'name', category),
      value: category,
    }))
  ),
  {
    help: 'This field must be filled out for compliance',
    required: true,
  }
);

/**
 * HELPER METHODS
 */

// A wrapper function that takes the configs generated by the above methods and formats them according to what react-config-builder
// needs.
/**
 * A single block checkbox. Yields a boolean for the component that uses it.
 *
 * @param {Object} optionsObject - an object containing additional optional fields, the options being:
 *    @param {String} optionsObject.fieldType -
 *    @param {String} optionsObject.fieldLabel -
 *    @param {String} optionsObject.fieldName -
 *    @param {String} optionsObject.dataType -
 *    @param {Boolean} optionsObject.[isProp=true] - Whether the input should be passed as a prop to the component. Pass false for non-prop attrs.
 *    @param {Array} optionsObject.items - The select options.
 *    @param {Array} optionsObject.options - The checkbox options.
 *    @param {Boolean} optionsObject.inline - Display checkboxes and radios inline not stacked.
 *    @param {Boolean} optionsObject.defaultChecked - Whether or not the checkbox is checked by default.
 *    @param {String} optionsObject.singularLabel - Label for each input of a repeatable.
 *    @param {Array} optionsObject.[elements=[]] - An editing config to use for each item in a repeatable.
 *    @param {String} optionsObject.help - Text for a help tooltip. If not provided, no tooltip is rendered.
 *    @param {String} optionsObject.validationType - Validation preconfiguration.
 *    @param {String} optionsObject.help - help text, which appears in a tooltip
 *
 * @returns {Object} - a view config intended for GridForm and ConfigBuilder.
 */
export const generateSettingUIConfigFromSpec = ({
  fieldType,
  fieldLabel,
  fieldName,
  dataType,
  isProp = true,
  items,
  options,
  inline,
  singularLabel,
  elements = [],
  help,
  placeholder,
  validationType,
  defaultChecked,
}) => ({
  label: fieldLabel,
  viewType: fieldType,
  value: '{{0}}',
  subType: dataType === 'string' ? 'text' : dataType,
  formatType: dataType === 'string' ? 'none' : dataType,
  items,
  options,
  inline,
  keys: [
    {
      key: isProp ? `form.props.${fieldName}` : `form.${fieldName}`,
    },
  ],
  singularLabel,
  elements,
  help,
  placeholder,
  getValidationConfig: getValidationFn(validationType),
  defaultChecked,
});

/**
 * Resolve the path for the current repeatable in relation to its parent configuration key, using the format defined by
 * generateSettingUIConfigFromSpec
 */
export const findPathInParent = (parentKey, i) => (elem) => {
  const {
    keys: [{ key: repeatableKey }],
  } = elem;
  const key = repeatableKey.replace('form.props', `${parentKey}.${i}`);
  return {
    ...elem,
    keys: [{ key }],
  };
};

/**
 * Retrieves the editing config for a given component.
 * Will use component's getEditingConfig function if provided.
 *
 * @param {object} options
 *  @param {object} options.Component - the component class/function.
 *  @param {object} options.componentDocument - the current component config.
 *    Passed to component editing configuration creator function.
 *
 * @returns {Array} - an editing config
 *
 * @example
 *  // A dynamic editing config:
 *  ComponentB.getEditingConfig = (componentDocument) => [
 *    boolCheckboxSpec('useDefaultSettings', 'Use defaults'),
 *    ...componentDocument.props.useDefaultSettings
 *      ? []
 *      : [stringInputSpec('customTitle', 'Custom Title')],
 *  ];
 *
 *  generateEditingConfig({
 *    Component: ComponentB,
 *    componentDocument: { props: { useDefaultSettings: true } },
 *  });
 *  // returns [boolCheckboxSpec('useDefaultSettings', 'Use defaults')];
 *
 *  generateEditingConfig({
 *    Component: ComponentB,
 *    componentDocument: {}
 *  });
 *  // returns [boolCheckboxSpec(...), stringInputSpec(...)]
 */
export const generateEditingConfig = ({
  Component = {},
  componentDocument = {},
}) =>
  typeof Component.getEditingConfig === 'function'
    ? Component.getEditingConfig(componentDocument)
    : [];

export const editingForm = (Component, componentDocument) =>
  generateEditingConfig({
    Component,
    componentDocument,
  }).map(generateSettingUIConfigFromSpec);

export const getEditingFieldsByName = (Component) => {
  const fields = generateEditingConfig({ Component });
  return (name) => fields.find((x) => x.fieldName === name);
};
